import { Popover, PopoverContent, PopoverTrigger } from '@/ui/shadcn/popover';
import { TagBadge, type TagLook } from '../TagBadge';
import styles from './TagLine.module.css';

/** One tag in a `TagLine`: its look already worked out (`tagLook`), since `src/ui` reads no `src/lib/tags`. */
export interface TagLineItem {
    readonly id: number | string;
    readonly name: string;
    readonly look: TagLook;
}

export interface TagLineProps {
    readonly tags: readonly TagLineItem[];
    /** How many tags the line shows before *+N*. */
    readonly max?: number;
    readonly 'data-testid': string;
}

/**
 * A record's tags in a table row (the tag design of the UI revamp preview): one line — the first `max` tags, each
 * cut at 15 characters with its full name on hover, then a **+N** button that opens every tag in a popover — so a
 * row with many tags is as tall as its neighbours. The button and the popover carry `data-row-control`, so pressing
 * them does not open the row (`recordRow.tsx`). Renders nothing for no tags. Test ids: the line `<testId>`, the
 * button `<testId>-more`, the popover `<testId>-all`.
 */
export function TagLine({ tags, max = 2, 'data-testid': testId }: TagLineProps) {
    if (tags.length === 0) {
        return null;
    }
    const shown = tags.slice(0, max);
    const rest = tags.length - shown.length;
    return (
        <span className={styles.line} data-testid={testId}>
            {shown.map((tag) => (
                <TagBadge fit="cut" key={tag.id} look={tag.look}>
                    {tag.name}
                </TagBadge>
            ))}
            {rest > 0 ? (
                <Popover>
                    <PopoverTrigger asChild>
                        <button
                            // The name starts with the visible "+N" (WCAG 2.5.3 Label in Name), then says what it does.
                            aria-label={`+${rest}: show all ${tags.length} tags`}
                            className={styles.more}
                            data-row-control
                            data-testid={`${testId}-more`}
                            type="button"
                        >
                            +{rest}
                        </button>
                    </PopoverTrigger>
                    <PopoverContent
                        align="start"
                        className={styles.popover}
                        data-row-control
                        data-testid={`${testId}-all`}
                    >
                        <p className={styles.caption}>All {tags.length} tags</p>
                        <ul className={styles.all}>
                            {tags.map((tag) => (
                                <li key={tag.id}>
                                    <TagBadge look={tag.look}>{tag.name}</TagBadge>
                                </li>
                            ))}
                        </ul>
                    </PopoverContent>
                </Popover>
            ) : null}
        </span>
    );
}
